/**
 * The PiP voice session state machine (#1307; promoted from #1217).
 *
 * Everything that decides WHAT the Owner sees lives here, behind injected
 * dependencies, so every transition in design grammar §15.2 — including the
 * two that are hard to reach by hand, permission-denied and agent-never-
 * arrives — is provable in a unit test with no browser, no WebRTC and no
 * server. `livekitRoom.ts` holds the SDK; `usePipVoiceSession.ts` holds React.
 *
 * Order of operations is deliberate: **microphone permission first, room
 * second.** `POST /api/livekit/token-v2` is not a read — it dispatches a
 * Simurgh worker before it answers (and, flag-off, MERGEs a Neo4j
 * conversation node). Asking the browser first means an Owner who declines
 * the mic leaves no orphan conversation behind and burns no agent process.
 *
 * #1307 additions over the promoted #1217 machine:
 *  - mute (widget control; snapshot flag, not a state)
 *  - duplex `sendText` — typed turns onto `lk.chat`, with C-27 degradation
 *    (SFU-refused publish resolves `duplex: 'unavailable'`, composer pauses)
 *  - live transcription fan-out (`onTranscript`), the in-thread live path
 *  - the minted conversation id kept on the snapshot (the thread record)
 *  - `mint` may receive a `roomName` (LMC resume/re-attach; RokHop ignores it)
 */
import {
  DuplexSendError,
  IDLE_SNAPSHOT,
  isLive,
  VoiceMintError,
  type RoomHandle,
  type TranscriptSegment,
  type VoiceFailure,
  type VoiceGrant,
  type VoiceSnapshot,
  type VoiceState,
} from './types'
// [#1314] One microphone, one surface — see voiceMutex.ts.
import { claimVoice, releaseVoice } from '../voiceMutex'

export interface VoiceSessionDeps {
  /** `navigator.mediaDevices.getUserMedia({ audio: true })`, injectable. */
  requestMic: () => Promise<MediaStream>
  mint: (opts: {
    sourceConversationId?: string | null
    roomName?: string | null
  }) => Promise<VoiceGrant>
  openRoom: () => RoomHandle
  now?: () => number
  /**
   * How long the agent gets to join before we call it absent. LMC’s shipped
   * web voice surface uses 30s and Simurgh pre-warms idle processes, so 30s
   * is a real timeout rather than a cold-start trap.
   */
  agentTimeoutMs?: number
  /** When the connect starts owing the Owner a sentence (§16.3). */
  slowAfterMs?: number
}

export interface StartOptions {
  sourceConversationId?: string | null
  /** Re-attach to an existing session’s room (LMC resume; Â§15.6 rule 1). */
  roomName?: string | null
}

export interface VoiceSession {
  getSnapshot(): VoiceSnapshot
  subscribe(listener: () => void): () => void
  /** Begin. A no-op while a conversation is already live. */
  start(opts?: StartOptions): Promise<void>
  /** Hang up. Safe from any state. */
  end(): Promise<void>
  /** Clear an `ended` / `failed` halo back to `idle`. */
  reset(): void
  /** Release timers and the room. For unmount. */
  dispose(): void
  /** #1307: mute/unmute the mic (widget control). */
  setMuted(muted: boolean): void
  /**
   * #1307 duplex: deliver one typed turn to the live session. Throws
   * DuplexSendError('not-live') with no session, DuplexSendError('rejected')
   * when the SFU refuses the publish (C-27 — the caller pauses the composer).
   * On success the typed turn is also fanned out as a final user transcript
   * segment so it appears in-thread like any Owner turn (§15.6 rule 3).
   */
  sendText(text: string): Promise<void>
  /** #1307: live transcript segments (both speakers). Returns unsubscribe. */
  onTranscript(cb: (segment: TranscriptSegment) => void): () => void
}

const DEFAULT_AGENT_TIMEOUT_MS = 30_000
const DEFAULT_SLOW_AFTER_MS = 10_000
const LEVEL_SAMPLE_MS = 120 // §15.2: activity ring tracks level on a 120ms lerp

/**
 * Map a getUserMedia rejection onto the sentence the Owner should read.
 *
 * The two cases are genuinely different and must not collapse: a DENIED mic
 * is recoverable by the Owner (allow it, try again), a MISSING mic is not
 * (there is no microphone), and telling someone to change a permission they
 * do not have is the kind of instruction that erodes trust in everything else
 * the product says. Anything unrecognised lands on 'unavailable' rather than
 * blaming the Owner’s browser for a fault we have not identified.
 */
export function classifyMicError(error: unknown): VoiceFailure {
  const name = (error as { name?: string } | null)?.name ?? ''
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'mic-denied'
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return 'mic-missing'
  }
  return 'unavailable'
}

export function createVoiceSession(deps: VoiceSessionDeps): VoiceSession {
  const now = deps.now ?? (() => Date.now())
  const agentTimeoutMs = deps.agentTimeoutMs ?? DEFAULT_AGENT_TIMEOUT_MS
  const slowAfterMs = deps.slowAfterMs ?? DEFAULT_SLOW_AFTER_MS

  let snapshot: VoiceSnapshot = IDLE_SNAPSHOT
  const listeners = new Set<() => void>()
  const transcriptListeners = new Set<(segment: TranscriptSegment) => void>()
  let typedSeq = 0

  let room: RoomHandle | null = null
  let connectedAt: number | null = null
  let agentTimer: ReturnType<typeof setTimeout> | null = null
  let slowTimer: ReturnType<typeof setTimeout> | null = null
  let levelTimer: ReturnType<typeof setInterval> | null = null
  let disposed = false
  /**
   * Increments on every start() and on every teardown. A callback from a room
   * we have already walked away from must not move the halo — without this,
   * a late `onClosed` from an abandoned connect drags a fresh attempt into
   * 'ended'.
   */
  let epoch = 0

  function emit(next: Partial<VoiceSnapshot>): void {
    snapshot = { ...snapshot, ...next }
    listeners.forEach((l) => l())
  }

  function emitTranscript(segment: TranscriptSegment): void {
    transcriptListeners.forEach((l) => l(segment))
  }

  function clearTimers(): void {
    if (agentTimer) clearTimeout(agentTimer)
    if (slowTimer) clearTimeout(slowTimer)
    if (levelTimer) clearInterval(levelTimer)
    agentTimer = null
    slowTimer = null
    levelTimer = null
  }

  async function teardown(): Promise<void> {
    epoch += 1
    clearTimers()
    // [#1314] Owner-scoped, so a late teardown from a superseded attempt cannot
    // free the mic out from under the surface that legitimately holds it.
    releaseVoice('pip')
    const closing = room
    room = null
    connectedAt = null
    if (closing) {
      try {
        await closing.close()
      } catch {
        /* the room is going away either way */
      }
    }
  }

  function fail(failure: VoiceFailure): void {
    emit({ state: 'failed', failure, level: 0, slow: false, durationMs: null, connectedAt: null })
  }

  function finish(): void {
    const durationMs = connectedAt === null ? 0 : Math.max(0, now() - connectedAt)
    emit({ state: 'ended', failure: null, level: 0, slow: false, durationMs })
  }

  function startLevelSampling(): void {
    if (levelTimer) clearInterval(levelTimer)
    levelTimer = setInterval(() => {
      if (!room) return
      // Only the two talking states carry amplitude. Reading a level into
      // 'connecting' would animate a ring that is meant to be an arc.
      const state: VoiceState = snapshot.state
      if (state !== 'listening' && state !== 'speaking') return
      let value: number
      try {
        value = room.level()
      } catch {
        value = 0
      }
      const clamped = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0
      if (Math.abs(clamped - snapshot.level) > 0.01) emit({ level: clamped })
    }, LEVEL_SAMPLE_MS)
  }

  async function start(opts: StartOptions = {}): Promise<void> {
    if (disposed) return
    if (isLive(snapshot.state)) return

    // Teardown FIRST: it releases our own claim, so claiming before it would
    // hand the mic straight back.
    await teardown()

    // [#1314] One microphone, one surface. The legacy voice page mounts its own
    // LiveKitRoom and knew nothing about this session (or the reverse), so both
    // could hold the mic at once — two agents transcribing the same speech into
    // two conversations. Refuse rather than steal: hanging up someone’s live
    // call to start another is worse than declining the second one.
    if (!claimVoice('pip')) {
      fail('unavailable')
      return
    }
    const mine = epoch
    const stale = () => disposed || mine !== epoch

    emit({
      state: 'requesting',
      failure: null,
      level: 0,
      durationMs: null,
      slow: false,
      muted: false,
      conversationId: null,
      threadId: opts.sourceConversationId ?? null,
      connectedAt: null,
      duplex: 'unknown',
    })

    // 1 — permission. Held only long enough to prove we have it; LiveKit
    // acquires its own track and the browser will not prompt twice.
    let probe: MediaStream
    try {
      probe = await deps.requestMic()
    } catch (error) {
      if (stale()) return
      fail(classifyMicError(error))
      return
    }
    try {
      probe.getTracks().forEach((t) => t.stop())
    } catch {
      /* a fake stream in a test, or a track already ended */
    }
    if (stale()) return

    // 2 — room + agent dispatch + token, in one backend call.
    emit({ state: 'connecting' })
    slowTimer = setTimeout(() => {
      if (!stale() && snapshot.state === 'connecting') emit({ slow: true })
    }, slowAfterMs)

    let grant: VoiceGrant
    try {
      grant = await deps.mint({
        sourceConversationId: opts.sourceConversationId ?? null,
        roomName: opts.roomName ?? null,
      })
    } catch (error) {
      if (stale()) return
      clearTimers()
      fail(error instanceof VoiceMintError ? error.kind : 'network')
      return
    }
    if (stale()) return
    // The minted room IS the conversation record (arch §5.1) — expose it so
    // the thread view and caption hydration can target it. TRF (#1731): the
    // host may name the record separately from the room.
    emit({ conversationId: grant.conversationId ?? grant.roomName })

    // 3 — join, publish, and wait for the agent to show up.
    const handle = deps.openRoom()
    room = handle

    handle.onAgentPresent(() => {
      if (stale()) return
      if (agentTimer) {
        clearTimeout(agentTimer)
        agentTimer = null
      }
      if (slowTimer) {
        clearTimeout(slowTimer)
        slowTimer = null
      }
      if (connectedAt === null) connectedAt = now()
      emit({ state: 'listening', slow: false, connectedAt })
      startLevelSampling()
    })

    handle.onAgentSpeaking((speaking) => {
      if (stale()) return
      if (snapshot.state !== 'listening' && snapshot.state !== 'speaking') return
      emit({ state: speaking ? 'speaking' : 'listening', level: speaking ? snapshot.level : 0 })
    })

    handle.onTranscription((segment) => {
      if (stale()) return
      emitTranscript(segment)
    })

    handle.onClosed(() => {
      if (stale()) return
      // A close BEFORE the agent ever arrived is a failure to connect, not a
      // conversation that ended — "Ended · 0 min" would be a lie.
      if (snapshot.state === 'connecting') {
        clearTimers()
        void teardown()
        fail('agent-absent')
        return
      }
      if (isLive(snapshot.state)) {
        clearTimers()
        finish()
        void teardown()
      }
    })

    try {
      await handle.connect(grant.url, grant.token)
      if (stale()) return
      await handle.publishMic()
    } catch (error) {
      if (stale()) return
      clearTimers()
      await teardown()
      fail(classifyMicError(error) === 'unavailable' ? 'network' : classifyMicError(error))
      return
    }
    if (stale()) return

    agentTimer = setTimeout(() => {
      if (stale()) return
      if (snapshot.state !== 'connecting') return
      void teardown()
      fail('agent-absent')
    }, agentTimeoutMs)
  }

  async function end(): Promise<void> {
    if (!isLive(snapshot.state)) return
    const reached = snapshot.state === 'listening' || snapshot.state === 'speaking'
    clearTimers()
    if (reached) finish()
    else emit({ state: 'idle', failure: null, level: 0, durationMs: null, slow: false, connectedAt: null })
    await teardown()
  }

  function setMuted(muted: boolean): void {
    if (snapshot.muted === muted) return
    // Optimistic: the control reflects intent immediately; a failed toggle
    // leaves the mic in its previous state and the next toggle retries.
    emit({ muted })
    const handle = room
    if (handle) {
      void handle.setMicEnabled(!muted).catch(() => {
        emit({ muted: !muted })
      })
    }
  }

  async function sendText(text: string): Promise<void> {
    const trimmed = text.trim()
    if (!trimmed) return
    const handle = room
    const live = snapshot.state === 'listening' || snapshot.state === 'speaking'
    if (!handle || !live) {
      throw new DuplexSendError('not-live', 'No live voice session to receive the message.')
    }
    try {
      await handle.sendText(trimmed)
    } catch (error) {
      // C-27: the refused publish is the degradation signal — a session
      // minted before the duplex grant. Resolve it so the composer pauses
      // instead of retrying into a wall.
      emit({ duplex: 'unavailable' })
      throw new DuplexSendError('rejected', `Typed message was refused: ${String(error)}`)
    }
    emit({ duplex: 'available' })
    // Echo the typed turn into the live transcript path so it appears
    // in-thread like any Owner turn (§15.6 rule 3). The durable record comes
    // from the agent’s writer (arch Â§5.1); this is the immediacy half.
    typedSeq += 1
    emitTranscript({
      id: `typed-${epoch}-${typedSeq}`,
      text: trimmed,
      final: true,
      speaker: 'user',
    })
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    start,
    end,
    reset() {
      if (isLive(snapshot.state)) return
      emit(IDLE_SNAPSHOT)
    },
    dispose() {
      disposed = true
      listeners.clear()
      transcriptListeners.clear()
      void teardown()
    },
    setMuted,
    sendText,
    onTranscript(cb) {
      transcriptListeners.add(cb)
      return () => {
        transcriptListeners.delete(cb)
      }
    },
  }
}
